import _ from 'lodash';

import Entity from './Entity';

import { stateFieldOptions } from './util';

import ParkerAccessDevice from './ParkerAccessDevice';
import ParkerVehicle from './ParkerVehicle';
import ParkerStatusHistory from './ParkerStatusHistory';
import Payor from './Payor';
import Rate from './Rate';
import Subscription from './Subscription';


const parkerEntityFieldsWithDynamicAllowedValues = {
  status: { enumName: 'ParkerStatusEnum' },
  suspension_status: { enumName: 'SuspensionStatusEnum' },
  termination_status: { enumName: 'TerminationStatusEnum' },
  access_system_status: { enumName: 'ParkerAccessSystemStatusEnum' },
  action: { enumName: 'BulkParkerActionEnum' },
  parkerStatusHistoryAction: { enumName: 'ParkerStatusHistoryActionEnum' }
};

const parkerEntityFieldsWithStaticAllowedValues = {
  state: {
    options: stateFieldOptions
  }
};


export default class Parker {
  fields = {
    id: {
      value: null
    },
    rate_id: {
      value: null
    },
    subscription_id: {
      value: null
    },
    suspension_status: {
      value: null
    },
    termination_status: {
      value: null
    },
    access_system_status: {
      value: null
    },
    first_name: {
      value: null
    },
    last_name: {
      value: null
    },
    email: {
      value: null
    },
    address: {
      value: null
    },
    city: {
      value: null
    },
    state: {
      value: null
    },
    zip: {
      value: null
    },
    phone: {
      value: null
    },
    parking_reason: {
      value: null
    },
    group: {
      value: null
    },
    pool: {
      value: null
    },
    passback: {
      value: null
    },
    reserved_space: {
      value: null
    },
    barcode: {
      value: null
    },
    nearest_suspension: {
      value: null
    },
    nearest_termination: {
      value: null
    },
    parker_status_history: {
      value: null
    },
    access_system_meta: {
      value: null
    },
    vehicles: {
      value: []
    },
    payor: {
      value: null,
    },
    rate: {
      value: null
    },
    subscription: {
      value: null
    },
    access_devices: {
      value: []
    },
    created_at: {
      value: null
    },
    updated_at: {
      value: null
    }
  };

  static ACCESS_SYSTEM_STATUS = {
    ON: 'ON',
    OFF_MANUAL: 'OFF_MANUAL',
  }

  static createNewPendingParker() {
    const parker = new this();
    parker.fields.suspension_status.value = 'PENDING';
    parker.fields.termination_status.value = 'PENDING';

    return parker;
  }


  static getFieldAllowedValues(fields) {
    return Entity.getFieldAllowedValues(
      parkerEntityFieldsWithStaticAllowedValues,
      parkerEntityFieldsWithDynamicAllowedValues,
      fields
    );
  }


  static createFromQueryValues(values) {
    const parker = new this();

    for (let field in parker.fields) {
      if (values.hasOwnProperty(field)) {
        switch (field) {
          case 'created_at':
          case 'updated_at':
            parker.fields[field].value = (values[field] === null) ?
              values[field] :
              new Date(Number(values[field]));
            break;
          case 'vehicles':
            if (Array.isArray(values[field])) {
              values[field].forEach(vehicle => {
                parker.fields[field].value.push(ParkerVehicle.createFromQueryValues(vehicle));
              });
            }
            break;
          case 'payor':
            parker.fields[field].value = (values[field] === null) ?
              values[field] :
              Payor.createFromQueryValues(values[field]);
            break;
          case 'rate':
            parker.fields[field].value = (values[field] === null) ?
              values[field] :
              Rate.createFromQueryValues(values[field]);
            break;
          case 'subscription':
            parker.fields[field].value = (values[field] === null) ?
              values[field] :
              Subscription.createFromQueryValues(values[field]);
            break;
          case 'access_devices':
            if (Array.isArray(values[field])) {
              values[field].forEach(accessDevice => {
                parker.fields[field].value.push(ParkerAccessDevice.createFromQueryValues(accessDevice));
              });
            }
            break;
          default:
            parker.fields[field].value = values[field];
        }
      }
    }

    return parker;
  }


  getQueryFieldValues() {
    const values = _.cloneDeep(ParkerInput);

    for (let field in values) {
      switch (field) {
        case 'id':
          if (this.fields[field].value === null) {
            delete values.id;
          }
          else {
            values[field] = this.fields[field].value;
          }
          break;
        case 'access_devices':
        case 'vehicles':
          if (Array.isArray(this.fields[field].value)) {
            values[field] = this.fields[field].value.map(entity =>
              entity.getQueryFieldValues()
            );
          }
          break;
        case 'parker_status_history':
          if (Array.isArray(this.fields[field].value)) {
            values[field] = this.fields[field].value.map(statusHistory => 
              ParkerStatusHistory.getQueryFieldValues(statusHistory)
            );
          }
          break;
        default:
          values[field] = this.fields[field].value;
      }
    }

    return values;
  }


  validate() {
    const requiredFields = [
      'first_name', 'last_name', 'email', 'rate_id', 'access_devices'
    ];

    for (let field of requiredFields) {
      const value = this.fields[field].value;
      switch (field) {
        case 'email':
          if (value == null || !/\S+@\S+\.\S+/.test(value)) {
            return { valid: false, error: 'Invalid email' };
          }
          break;
        default:
          if (value == null) {
            return { valid: false, error: 'Missing required parker information' };
          }
      }
    }

    return { valid: true };
  }
}


export const ParkerInput = {
  id: null,
  first_name: null,
  last_name: null,
  email: null,
  address: null,
  city: null,
  state: null,
  zip: null,
  phone: null,
  parking_reason: null,
  group: null,
  pool: null,
  passback: null,
  reserved_space: null,
  access_devices: null,
  vehicles: null,
  parker_status_history: null,
}


export class ParkerMeta {
  fields = {
    id: {
      value: null
    },
    first_name: {
      value: null
    },
    last_name: {
      value: null
    },
    group: {
      value: null
    },
    vehicles: {
      value: []
    },
    selected_start_date: {
      value: null
    },
    selected_end_date: {
      value: null
    }
  };


  static createFromQueryValues(values) {
    const parkerMeta = new this();

    for (let field in parkerMeta.fields) {
      if (values.hasOwnProperty(field)) {
        switch (field) {
          case 'selected_start_date':
          case 'selected_end_date':
            parkerMeta.fields[field].value = (values[field] === null) ?
              values[field] :
              new Date(Number(values[field]));
            break;
          case 'vehicles':
            if (Array.isArray(values[field])) {
              for (let vehicle of values[field]) {
                parkerMeta.fields[field].value.push(vehicle);
              }
            }
            break;
          default:
            parkerMeta.fields[field].value = values[field];
        }
      }
    }

    return parkerMeta;
  }


  getLineItemParkerMetaValues() {
    const values = _.cloneDeep(LineItemParkerMeta);

    values.id = this.fields.id.value;

    const valueSelectedStartDate = this.fields.selected_start_date.value;
    if (valueSelectedStartDate !== null) {
      const valueYear = valueSelectedStartDate.getUTCFullYear();
      const valueMonth = (valueSelectedStartDate.getUTCMonth() + 1).toString().padStart(2, '0');
      const valueDate = valueSelectedStartDate.getUTCDate().toString().padStart(2, '0');
      const valueString = `${valueYear}-${valueMonth}-${valueDate}`;

      values.selected_start_date = valueString;
    }
    else {
      delete values.selected_start_date;
    }

    const valueSelectedEndDate = this.fields.selected_end_date.value;
    if (valueSelectedEndDate !== null) {
      const valueYear = valueSelectedEndDate.getUTCFullYear();
      const valueMonth = (valueSelectedEndDate.getUTCMonth() + 1).toString().padStart(2, '0');
      const valueDate = valueSelectedEndDate.getUTCDate().toString().padStart(2, '0');
      const valueString = `${valueYear}-${valueMonth}-${valueDate}`;

      values.selected_end_date = valueString;
    }
    else {
      delete values.selected_end_date;
    }

    return values;
  }
}


export const LineItemParkerMeta = {
  id: null,
  selected_start_date: null,
  selected_end_date: null
}

export const ParkerActions = {
  ACTIVATE_PENDING_PARKERS: 'ACTIVATE_PENDING_PARKERS',
  DELETE_PENDING_PARKERS: 'DELETE_PENDING_PARKERS'
}
